import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { sleep } from '~/shared';
export class Requester {
	private pendingRequestsCount = 0;

	constructor(
		private readonly retryLimit = 3,
		private readonly initialBackoff = 1000,
	) {}

	public getPendingRequestsCount(): number {
		return this.pendingRequestsCount;
	}

	public async request<T, D = unknown>(
		config: AxiosRequestConfig<D>,
		shouldRetry = true,
	): Promise<AxiosResponse<T, D>> {
		let attempts = 0;
		while (attempts < this.retryLimit) {
			try {
				this.pendingRequestsCount++;

				const response = await axios<T, AxiosResponse<T, D>, D>(config);
				if (attempts > 0) {
					console.log(
						`[Requester] Request to "${config.url}" succeeded after ${attempts} attempts`,
					);
				}

				return response;
			} catch (error) {
				if (!this.shouldRetry(error) || !shouldRetry) {
					throw error;
				}

				if (attempts === 0) {
					console.error(
						`[Requester] Request to "${
							config.url
						}" failed, retrying... Error: ${JSON.stringify(
							error,
							Object.getOwnPropertyNames(error),
						)}`,
					);
				}

				await sleep(this.getBackoffTime(attempts));
				attempts++;
			} finally {
				this.pendingRequestsCount--;
			}
		}

		throw new Error(
			`[Requester] Request to "${config.url}" failed after ${this.retryLimit} attempts`,
		);
	}

	public shouldRetry(error: any): boolean {
		if (!error.response) {
			return true;
		}

		if (['ECONNABORTED', 'ECONNRESET'].includes(error.code)) {
			return true;
		}

		if (error.response?.status >= 500) {
			return true;
		}

		return false;
	}

	public getBackoffTime(attempt: number): number {
		return this.initialBackoff * Math.pow(2, attempt);
	}
}
